/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useState, useEffect } from 'react';
import { Notification, User, UserRole, NotificationType } from '../domain/entities';
import { WorkflowRequest, RequestStatus } from '../modules/rider-ops/types';
import { getRepository } from '../config/repository';

export function useWorkflow(user: User | null) {
  const [requests, setRequests]           = useState<WorkflowRequest[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading]             = useState(true);

  const refresh = async () => {
    if (!user) {
      setRequests([]);
      setNotifications([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const repo = getRepository();
      const departments = await repo.getDepartments();
      // Only admins / dept heads can call GET /api/riders/requests (rider:approve_requests).
      // Managers & Employees must NOT fetch — they use the Submit Request flow instead.
      const canFetchRequests =
        user.role === UserRole.ADMIN ||
        user.role === UserRole.DEPARTMENT_HEAD ||
        user.role === (UserRole as any).SUPER_ADMIN ||
        user.role === (UserRole as any).PLATFORM_OWNER;
      const [allRequests, userNotifications] = await Promise.all([
        canFetchRequests
          ? repo.getRequests().catch(() => [] as WorkflowRequest[])
          : Promise.resolve([] as WorkflowRequest[]),
        repo.getNotifications(user.id, {
          assignedDepartments: user.assignedDepartments,
          departments: departments.map(d => ({ id: d.id, name: d.name })),
          assignedBrands: user.assignedBrands,
        }).catch(() => [] as Notification[]),
      ]);
      setRequests(allRequests);
      setNotifications(userNotifications);
    } catch {
      setRequests([]);
      setNotifications([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    refresh();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // ── Scenario A: Store Manager submits a request ────────────────────────────
  // Recipients: ALL admins of the same org.
  // Anti-self: actor (user) is skipped even if they happen to also be an Admin.
  const submitRequest = async (
    requestData: Omit<WorkflowRequest, 'id' | 'status' | 'createdAt' | 'requesterId' | 'requesterName'>
  ) => {
    if (!user) return;

    const newRequest: WorkflowRequest = {
      ...requestData,
      id: crypto.randomUUID(),
      status: RequestStatus.PENDING,
      requesterId: user.id,
      requesterName: user.name,
      createdAt: Date.now(),
    };
    await getRepository().saveRequest(newRequest);

    // Find all admins in the same org
    const orgId    = newRequest.orgId ?? (await getRepository().getCurrentOrgId()) ?? '';
    const allUsers = await getRepository().getUsers();
    const orgAdmins = allUsers.filter(u => u.role === UserRole.ADMIN && u.orgId === orgId);

    for (const admin of orgAdmins) {
      if (admin.id === user.id) continue; // anti-self rule
      await getRepository().saveNotification({
        id: crypto.randomUUID(),
        recipientId: admin.id,
        actorId: user.id,
        type: NotificationType.NEW_REQUEST,
        title: 'New Request Submitted',
        message: `${user.name} submitted a ${requestData.type.replace(/_/g, ' ').toLowerCase()} request.`,
        targetPath: '/apps/rider-manager/inbox',
        isRead: false,
        createdAt: Date.now(),
        orgId,
      });
    }

    await refresh();
  };

  // ── Scenario B: Admin approves or rejects a request ───────────────────────
  // Recipient: the Store Manager who submitted the request.
  // Anti-self: if somehow the actor is the same person as the requester, skip.
  const updateRequestStatus = async (
    requestId: string,
    status: RequestStatus,
    adminNote?: string,
    riderId?: string,
  ) => {
    const allRequests = await getRepository().getRequests();
    const request = allRequests.find(r => r.id === requestId);
    if (!request) return;

    const updatedRequest = {
      ...request,
      status,
      adminNote,
      riderId: riderId || request.riderId,
    };
    await getRepository().saveRequest(updatedRequest);

    // Anti-self: only notify if the actor is different from the requester
    if (!user || user.id !== request.requesterId) {
      const approved = status === RequestStatus.APPROVED;
      await getRepository().saveNotification({
        id: crypto.randomUUID(),
        recipientId: request.requesterId,
        actorId: user?.id,
        type: approved ? NotificationType.APPROVED : NotificationType.REJECTED,
        title: approved ? 'Request Approved' : 'Request Rejected',
        message: `Your ${request.type.replace(/_/g, ' ').toLowerCase()} request has been ${status.toLowerCase()}.`,
        targetPath: '/apps/rider-manager/database',
        riderId: riderId || request.riderId,
        isRead: false,
        createdAt: Date.now(),
        orgId: request.orgId ?? '',
      });
    }

    await refresh();
    return updatedRequest;
  };

  // ── Mark a single notification as read ────────────────────────────────────
  const markRead = async (notificationId: string) => {
    await getRepository().markNotificationRead(notificationId);
    await refresh();
  };

  // ── Mark ALL unread notifications as read (client-side batch) ─────────────
  const markAllRead = async () => {
    await Promise.all(
      notifications
        .filter(n => !n.isRead)
        .map(n => getRepository().markNotificationRead(n.id))
    );
    await refresh();
  };

  // ── Clear all notifications ───────────────────────────────────────────────
  const clearNotifications = async () => {
    try { await (getRepository() as any).clearNotifications(); } catch {}
    setNotifications([]);
  };

  return {
    requests,
    notifications,
    loading,
    submitRequest,
    updateRequestStatus,
    markRead,
    markAllRead,
    clearNotifications,
    refresh,
  };
}
